import axios from "axios";
import { authoringAxios } from "../components/apiCall";

const AUTHORING_ASK_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api-xgen.lexxtechnologies.com";

const MAX_CONTENT_LENGTH = 8000;

const validateContentLength = (content) => {
  const text = content || "";

  if (text.length > MAX_CONTENT_LENGTH) {
    const error = new Error(
      `Selected content is too long. Please select less than ${MAX_CONTENT_LENGTH} characters.`
    );

    error.code = "CONTENT_TOO_LONG";
    error.contentLength = text.length;
    error.maxLength = MAX_CONTENT_LENGTH;

    throw error;
  }

  return text;
};

const buildResponse = (input) => {
  const normalized = (input || "").toLowerCase();

  if (normalized.includes("operating pressure")) {
    return "The procedure references pressure controls but does not define a single operating pressure value. Add a validated value and tolerance range from your equipment specification.";
  }

  if (normalized.includes("summarize")) {
    return "Summary: The procedure confirms vessel readiness, verifies calibration inputs, executes survey data acquisition with quality controls, and enforces validation checks before data handover.";
  }

  if (normalized.includes("explain")) {
    return "This paragraph is defining prerequisites, required tools, and validation criteria so the survey output remains compliant and traceable.";
  }

  if (normalized.includes("safety")) {
    return "Suggested safety precautions: 1) Verify isolation boundaries before startup. 2) Confirm communication protocol with watch officer. 3) Log all anomalies immediately. 4) Stop operation if acceptance criteria are exceeded.";
  }

  return "I can help refine this procedure. You can ask for a summary, controlled-language rewrite, risk-focused expansion, or validation-ready terminology updates.";
};

export const getMockAiResponse = (input) =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve(buildResponse(input));
    }, 900);
  });

export const askAuthoringAi = async ({ question, documentId, sourceIds = [], rows = 5 }) => {
  const response = await authoringAxios.post(
    `${AUTHORING_ASK_BASE_URL}/api/v1/authoring/ask?rows=${rows}`,
    {
      question,
      document_id: documentId || "",
      source_ids: Array.isArray(sourceIds) ? sourceIds : [],
    },
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

export const validateAuthoringText = async ({ text, documentId, rows = 5 }) => {
  const response = await authoringAxios.post(
    `${AUTHORING_ASK_BASE_URL}/api/v1/authoring/validate?rows=${rows}`,
    {
      text: text || "",
      document_id: documentId || "",
    },
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

export const rewriteAuthoringContent = async ({ 
  content, 
  category = "Setup Instruction", 
  rows = 10, 
  debug = false,
}) => {
  const validatedContent = validateContentLength(content);

  console.log(
    `Calling Rewrite API with content length: ${validatedContent.length}/8000`
  );

  // console.log("Content length:", content?.length);

  const response = await authoringAxios.post(
    `${AUTHORING_ASK_BASE_URL}/api/v1/authoring/rewrite?rows=${rows}&debug=${debug}`,
    {
      content: content || "",
      category,
    },
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

export const controlledLanguageAuthoringContent = async ({
  content,
  category = "Setup Instruction",
  approved_documents = [],
  rows = 10,
  debug = false,
}) => {
  const response = await authoringAxios.post(
    `${AUTHORING_ASK_BASE_URL}/api/v1/authoring/controlled-language?rows=${rows}&debug=${debug}`,
    {
      content: content || "",
      category,
      approved_documents: Array.isArray(approved_documents)
        ? approved_documents
        : [],
    },
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

export const simplifyAuthoringContent = async ({ 
  content, 
  category = "Setup Instruction", 
  rows = 10,
  debug = false,
 }) => {
  const response = await authoringAxios.post(
    `${AUTHORING_ASK_BASE_URL}/api/v1/authoring/simplify?rows=${rows}&debug=${debug}`,
    {
      content: content || "",
      category,
    },
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

export const grammarAuthoringContent = async ({
   content,
   category = "Setup Instruction",
   rows = 10,
   debug = false,}) => {
  const response = await authoringAxios.post(
    `${AUTHORING_ASK_BASE_URL}/api/v1/authoring/grammar?rows=${rows}&debug=${debug}`,
    {
      content: content || "",
      category,
    },
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

export const draftAuthoringContent = async ({
  user_prompt,
  category = "Setup Instruction",
  approved_documents = [],
  rows = 10,
  debug = false,
  signal,
  timeoutMs = 30000,
}) => {
  console.log("[draftAuthoringContent] Request payload:", {
    user_prompt_length: user_prompt?.length,
    category,
    approved_documents,
    rows,
    debug,
  });
  
  const response = await authoringAxios.post(
    `${AUTHORING_ASK_BASE_URL}/api/v1/authoring/draft?rows=${rows}&debug=${debug}`,
    {
      user_prompt: user_prompt || "",
      category,
      approved_documents: Array.isArray(approved_documents)
        ? approved_documents
        : [],
    },
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      timeout: timeoutMs,
      signal,
    }
  );

  console.log("[draftAuthoringContent] Response received", {
    hasContent: !!response?.data?.content,
    hasProcedureDescription: !!response?.data?.procedureDescription,
    hasSteps: Array.isArray(response?.data?.steps),
    status: response?.status,
  });
  return response.data;
};

export const expandAuthoringContent = async ({
  content,
  category = "Setup Instruction",
  detail_level = "moderate",
  approved_documents = [],
  rows = 10,
  debug = false,
}) => {
  console.log("[expandAuthoringContent] Request payload:", {
    content_length: content?.length,
    category,
    detail_level,
    approved_documents,
    rows,
    debug,
  });

  const response = await authoringAxios.post(
    `${AUTHORING_ASK_BASE_URL}/api/v1/authoring/expand?rows=${rows}&debug=${debug}`,
    {
      content: content || "",
      category,
      detail_level,
      approved_documents: Array.isArray(approved_documents)
        ? approved_documents
        : [],
    },
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );

  console.log("[expandAuthoringContent] Response received", {
    hasContent: !!response?.data?.content,
    hasProcedureDescription: !!response?.data?.procedureDescription,
    hasSteps: Array.isArray(response?.data?.steps),
    status: response?.status,
  });
  return response.data;
};

export const generateAuthoringContent = async ({
  user_prompt,
  category = "Setup Instruction",
  approved_documents = [],
  rows = 10,
}) => {
  const response = await authoringAxios.post(
    `${AUTHORING_ASK_BASE_URL}/api/v1/authoring/generate?operation=generate&rows=${rows}`,
    {
      user_prompt,
      category,
      approved_documents: Array.isArray(approved_documents) ? approved_documents : [],
    },
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};